import { getErrorMessage } from '@/api/errors'
import { MemberAvatar } from '@/components/projects/MemberAvatar'
import { MemberStatusBadge } from '@/components/tenant-members/MemberStatusBadge'
import { Input } from '@/components/ui/input'
import { useProjectTeamMembers } from '@/hooks'
import { cn } from '@/lib/utils'
import { Search, XCircle } from 'lucide-react'
import { useEffect, useId, useMemo, useState, type FocusEvent } from 'react'

interface ProjectMemberPickerProps {
  projectId: string
  value: string | null
  onChange: (userId: string | null) => void
  valueLabel?: string | null
  placeholder?: string
  disabled?: boolean
  inputId?: string
}

export function ProjectMemberPicker({
  projectId,
  value,
  onChange,
  valueLabel,
  placeholder = 'Search user',
  disabled,
  inputId,
}: ProjectMemberPickerProps) {
  const { data: members = [], isLoading, isError, error, refetch } = useProjectTeamMembers(projectId)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const listId = useId()
  const selectionId = `${listId}-selection`

  const selected = useMemo(() => members.find(m => m.userId === value) ?? null, [members, value])
  const selectedLabel = selected ? selected.displayName || selected.email : value ? valueLabel || null : null

  const showSelectionInField = selectedLabel !== null && !open

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return members
    return members.filter(
      m => m.displayName.toLowerCase().includes(term) || m.email.toLowerCase().includes(term),
    )
  }, [members, search])

  const close = () => {
    setOpen(false)
    setSearch('')
    setActiveIndex(0)
  }

  const optionId = (index: number) => `${listId}-option-${index}`

  const commit = (userId: string) => {
    onChange(userId)
    close()
  }

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
    close()
  }

  useEffect(() => {
    if (open) void refetch()
  }, [open, refetch])

  useEffect(() => {
    if (!open) return
    document.getElementById(`${listId}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex, filtered.length, listId])

  // Radix Dialog listens for Escape on document with capture, so it would close the whole
  // dialog before a handler on this subtree ever runs. window capture runs earlier still.
  useEffect(() => {
    if (!open) return

    const dismissListOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setOpen(false)
      setSearch('')
    }

    window.addEventListener('keydown', dismissListOnEscape, { capture: true })
    return () => window.removeEventListener('keydown', dismissListOnEscape, { capture: true })
  }, [open])

  return (
    <div className="relative" onBlur={handleBlur}>
      <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

      <Input
        id={inputId}
        role="combobox"
        aria-expanded={open}
        aria-controls={open && filtered.length > 0 ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered.length > 0 ? optionId(activeIndex) : undefined}
        aria-label={inputId ? undefined : 'Search user'}
        aria-describedby={selectedLabel ? selectionId : undefined}
        placeholder={showSelectionInField ? '' : placeholder}
        value={search}
        disabled={disabled}
        className={cn('pl-9', value && !disabled && 'pr-10', showSelectionInField && 'caret-transparent')}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setSearch(e.target.value)
          setActiveIndex(0)
          setOpen(true)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()

            const active = open ? filtered[activeIndex] : undefined
            if (active) commit(active.userId)
            else close()
            return
          }

          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()

            if (!open) {
              setOpen(true)
              setActiveIndex(0)
              return
            }

            if (filtered.length === 0) return

            const step = e.key === 'ArrowDown' ? 1 : -1
            setActiveIndex((current) => (current + step + filtered.length) % filtered.length)
            return
          }

          if (!open || filtered.length === 0) return

          if (e.key === 'Home') {
            e.preventDefault()
            setActiveIndex(0)
            return
          }

          if (e.key === 'End') {
            e.preventDefault()
            setActiveIndex(filtered.length - 1)
          }
        }}
      />

      {showSelectionInField && (
        <div
          aria-hidden="true"
          data-testid="project-member-selected"
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 flex items-center gap-2 pl-9',
            value && !disabled ? 'right-10' : 'right-3',
          )}
        >
          <MemberAvatar name={selectedLabel} className="h-6 w-6" />
          <span className="truncate text-sm text-muted-foreground">{selectedLabel}</span>
        </div>
      )}

      {selectedLabel && (
        <span id={selectionId} className="sr-only">
          {`Currently assigned to ${selectedLabel}`}
        </span>
      )}

      {value && !disabled && (
        <button
          type="button"
          // Keeps focus on the input: this button unmounts on click, and a focus loss from a
          // removed element fires no blur, which would leave the list open with nothing to close it.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onChange(null)
            close()
          }}
          aria-label="Clear assignee"
          className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <XCircle className="h-5 w-5" />
        </button>
      )}

      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-border bg-background p-1 shadow-elevated">
          {isLoading ? (
            <div role="status" aria-live="polite" className="py-2 text-center text-sm text-muted-foreground">
              Loading...
            </div>
          ) : isError ? (
            <div role="alert" className="py-2 text-center text-sm text-destructive">
              {getErrorMessage(error) ?? 'Failed to load project members.'}
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="project-member-empty"
              className="py-2 text-center text-sm text-muted-foreground"
            >
              No users found.
            </div>
          ) : (
            <div id={listId} role="listbox" aria-label="Project members">
              {filtered.map((member, index) => {
              const label = member.displayName || member.email
              const isSelected = member.userId === value
              const isActive = index === activeIndex
              return (
                <button
                  key={member.userId}
                  id={optionId(index)}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={isSelected}
                  // Keeps focus on the input so the wrapper's blur does not close the list before this click lands.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => commit(member.userId)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left',
                    isActive && 'bg-muted',
                    isSelected && 'font-medium',
                  )}
                >
                  <MemberAvatar name={label} className="h-8 w-8" />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium">{label}</span>
                    <span className="truncate text-xs text-muted-foreground">{member.email}</span>
                  </div>
                  <div className="ml-auto shrink-0">
                    <MemberStatusBadge status={member.status} />
                  </div>
                </button>
              )
            })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
